import React from 'react';

import generateHreflang from 'lib/utils/generateHreflang';
import PATHS from 'lib/navigation/paths';
import { SLUGS_TYPES } from 'lib/utils/CONSTANTS';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { mapNamedLocaleToLocale } from 'lib/data/localesMap';
import { defaultNS } from 'next-i18next.config';
import useDetectSlugsCSR from 'views/common/hooks/useDetectSlugsCSR';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import { HydrationWrapper } from 'views/common/components';
import { getDeviceType } from 'lib/utils/getDeviceType';
import isCrawlers from 'lib/utils/isCrawlers';
import dynamic from 'next/dynamic';
import { handleRidesServerReequest } from 'views/rides-module/utils/handleRidesServerReequest';
import { checkIfUrlHasCorrectSegmentsTranslations } from 'lib/utils/checkIfUrlHasCorrectSegmentsTranslations';
import { ContentContext } from 'views/rebranded-module/common/context/useContent';
import { useRidesListingCMSData } from 'views/rebranded-module/common/motorcycle-rides-module/RidesViewV2/hooks/useRidesListingCMSData';
import { useRideDetailsCMSData } from 'views/rebranded-module/motorcycle-rides-module/RideDetailsViewV2/hooks/useRideDetailsCMSData';
import { RidesViewV2Skeleton } from 'views/rebranded-module/common/motorcycle-rides-module/RidesViewV2';

const RidesViewV2 = dynamic(
  () => import(
    'views/rebranded-module/common/motorcycle-rides-module/RidesViewV2'
  ).then((file) => file.RidesViewV2),
  {
    loading: () => <RidesViewV2Skeleton />,
  },
);

const RideDetailsViewV2 = dynamic(() => import(
  'views/rebranded-module/motorcycle-rides-module/RideDetailsViewV2'
).then((file) => file.RideDetailsViewV2));

const RidesPage = (props) => {
  const { serverData, slugSSR, rentalWidgetInit } = props;
  const ridescontent = useRidesListingCMSData();
  const detailscontent = useRideDetailsCMSData();
  const appNavigator = useAppNavigator();

  const detectSlugsReqBody = () => ({
    slugs: [
      {
        slug: appNavigator.query.slug,
        expected_types: [SLUGS_TYPES.attraction, SLUGS_TYPES.location],
      },
    ],
  });
  const [slug, loadingSLug, handleSlugTypeChange] = useDetectSlugsCSR(
    slugSSR,
    appNavigator.query.slug,
    detectSlugsReqBody(),
    PATHS.motorcycleRides.slug,
    [appNavigator.query.slug],
  );

  const renderPageView = () => {
    if (
      !appNavigator.query.slug
      || slug?.slugs[0].slug_type === SLUGS_TYPES.location
    ) {
      return (
        <ContentContext.Provider value={ridescontent?.data}>
          <RidesViewV2
            serverData={serverData}
            slugData={slug}
            handleSlugTypeChange={handleSlugTypeChange}
          />
        </ContentContext.Provider>
      );
    }
    if (slug?.slugs[0].slug_type === SLUGS_TYPES.attraction) {
      return (
        <ContentContext.Provider value={detailscontent?.data}>
          <RideDetailsViewV2
            serverData={serverData}
            slugData={slug}
            rentalWidgetInit={rentalWidgetInit}
          />
        </ContentContext.Provider>
      );
    }
    return <></>;
  };
  return (
    <>
      {loadingSLug ? (
        <HydrationWrapper>
          <RidesViewV2Skeleton />
        </HydrationWrapper>
      ) : (
        <HydrationWrapper>{renderPageView()}</HydrationWrapper>
      )}
    </>
  );
};

export async function getServerSideProps(ctx) {
  const { locale } = ctx;
  let deviceType = '';
  let hreflangData;
  const ssrRender = isCrawlers(ctx.req.headers['user-agent']);
  const pageOptions = {
    rebrandingPage: true,
    options: {
      transparentNavBar: true,
    },
  };
  if (ctx.query.slug) {
    hreflangData = generateHreflang(
      PATHS.motorcycleRides.slug(ctx.query.slug).url,
      locale,
    );
  } else {
    hreflangData = generateHreflang(PATHS.motorcycleRides.index().url, locale);
  }
  const serverResponse = await handleRidesServerReequest(
    ctx,
    hreflangData.canonical,
  );

  if (serverResponse.newPath) {
    return {
      redirect: {
        destination: serverResponse.newPath,
        permanent: true,
      },
    };
  }
  if (serverResponse.redirectUrl) {
    return {
      redirect: {
        destination: serverResponse.redirectUrl,
        permanent: true,
      },
    };
  }
  if (serverResponse?.serverError) {
    ctx.res.statusCode = serverResponse?.serverError.statusCode;
  }

  deviceType = getDeviceType(ctx.req.headers['user-agent']);

  const isCorrectSegemntTranslation = checkIfUrlHasCorrectSegmentsTranslations(
    ctx,
    hreflangData.canonical,
  );
  if (isCorrectSegemntTranslation.shouldRedirect) {
    return {
      redirect: {
        destination: isCorrectSegemntTranslation.redirectURL,
        permanent: true,
      },
    };
  }
  return {
    props: {
      ...(await serverSideTranslations(mapNamedLocaleToLocale[locale], [
        ...defaultNS,
        'rides_listing',
        'ride_details',
        'common_event_ride_card',
        'common_new_main_widget',
        'common_share',
        'common_participating_locations',
        'common_gallery',
        'fe_er_common_main_widget',
        'fe_er_expandable_description_component',
        'fe_er_common_map_with_groups',
        'fe_er_common_location_card',
        'fe_er_reviews_title',
        'fe_er_reviews_card',
        'fe_er_share_component',
        'fe_er_auth_overlay',
        'fe_er_auth',
        'fe_er_common_reviews',
        'fe_er_my_review',
      ])),
      ...serverResponse,
      ...pageOptions,
      ssrRender,
      hreflangData,
      deviceType,
    },
  };
}
export default RidesPage;
